import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { useI18n } from '@/shared/i18n/useT';
import { clearStaleBuildAttempt } from './staleBuildRecovery';

/**
 * The recovery path for a device whose stored copy of the app has gone bad.
 *
 * Without this the only remedy is unregistering the service worker from
 * DevTools, which is not something a non-technical user can be walked through —
 * and support cannot do it for them. The button is deliberately narrow: it drops
 * the worker and its caches and nothing else, so it cannot sign anyone out
 * (Supabase keeps the session in localStorage) or lose a preference. That makes
 * it safe to suggest to anyone who says the app looks wrong.
 */
export function ResetAppDataCard() {
  const { t } = useI18n();
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');

  async function reset() {
    setStatus('working');
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      clearStaleBuildAttempt();
    } catch {
      setStatus('error');
      return;
    }
    // Reload last and unconditionally: even a partial clear leaves the tab
    // running the code we just tried to replace.
    window.location.reload();
  }

  return (
    <Card title={t('settings.reset.title')} description={t('settings.reset.description')}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void reset()}
          disabled={status === 'working'}
          className="nt-control"
        >
          {status === 'working' ? t('settings.reset.working') : t('settings.reset.action')}
        </button>
        {status === 'error' && (
          <p role="alert" className="text-sm text-danger">
            {t('settings.reset.error')}
          </p>
        )}
      </div>
    </Card>
  );
}
